/**
 * One category-to-trade map for task cards, project tasks, and AI chat.
 * A vendor matches only when its main category or an extra category is
 * that trade. Descriptions, substrings, and a handyman catch-all do not.
 */

const {
  DEFAULT_VENDOR_CATEGORIES,
  isSettlementServiceCategory,
  looseCategoryKey,
  normalizeCategory,
  resolveVendorCategory,
} = require('./vendorCategories');

const TASK_CATEGORY_TRADES = {
  Electrical: 'Electrical',
  Plumbing: 'Plumbing',
  HVAC: 'HVAC',
  Pool: 'Pool Service',
  Roof: 'Roofing',
  Roofing: 'Roofing',
  'Gutters & Drainage': 'Roofing',
  Appliances: 'Appliance Repair',
  'Pest Control': 'Pest Control',
  Pest: 'Pest Control',
  Landscape: 'Landscaping',
  Landscaping: 'Landscaping',
  Lawn: 'Landscaping',
};

const UNMAPPED_TASK_CATEGORIES = [
  'Financial & Administrative',
  'Irrigation',
  'Exterior',
  'Windows & Doors',
  'Garage',
  'Safety',
  'Interior',
  'Exterior Hardscape',
  'Septic',
  'Well & Water Treatment',
  'Solar',
];

function tradeForTaskCategory(category) {
  const key = normalizeCategory(category);
  if (!key) return null;
  const name = Object.keys(TASK_CATEGORY_TRADES).find((label) => normalizeCategory(label) === key);
  return name ? TASK_CATEGORY_TRADES[name] : null;
}

function vendorTradeNames(vendor) {
  if (!vendor) return [];
  const names = [];
  if (Array.isArray(vendor.category)) names.push(...vendor.category);
  else if (vendor.category) names.push(vendor.category);
  if (Array.isArray(vendor.categories)) names.push(...vendor.categories);
  return names.map((name) => String(name || '').trim()).filter(Boolean);
}

function vendorMatchesTrade(vendor, trade) {
  const wanted = normalizeCategory(trade);
  if (!wanted) return false;
  return vendorTradeNames(vendor).some((name) => normalizeCategory(name) === wanted);
}

function vendorsMatchingTrade(vendors, trade) {
  if (!normalizeCategory(trade)) return [];
  return (vendors || []).filter((vendor) => vendorMatchesTrade(vendor, trade));
}

function primaryVendorTrade(vendor) {
  const names = vendorTradeNames(vendor);
  return names[0] || '';
}

function extraVendorTrades(vendor) {
  const primary = normalizeCategory(primaryVendorTrade(vendor));
  const extras = [];
  const seen = new Set();
  vendorTradeNames(vendor).forEach((name) => {
    const label = String(name || '').trim();
    const key = normalizeCategory(label);
    if (!key || key === primary || seen.has(key)) return;
    seen.add(key);
    extras.push(label);
  });
  return extras;
}

function blockedVendorCategory(name) {
  const key = looseCategoryKey(name);
  if (!key || isSettlementServiceCategory(name)) return true;
  return key === 'title company' || key.indexOf('title company ') === 0;
}

function categoryOptionLabel(name) {
  const raw = String(name || '').trim();
  if (!raw || blockedVendorCategory(raw)) return '';
  const resolved = resolveVendorCategory(raw);
  if (!resolved || resolved.settlement) return '';
  const label = resolved.name || raw;
  if (blockedVendorCategory(label)) return '';
  return label;
}

/**
 * Dropdown options: the shared home-service list, then custom categories
 * already stored on these vendors. Matching ignores case. Settlement
 * services are left out.
 */
function vendorCategoryOptions(vendors) {
  const options = [];
  const seen = new Set();
  const add = (value) => {
    const label = categoryOptionLabel(value);
    const key = looseCategoryKey(label);
    if (!label || !key || seen.has(key)) return;
    seen.add(key);
    options.push(label);
  };
  DEFAULT_VENDOR_CATEGORIES.forEach(add);
  const extras = [];
  const extraSeen = new Set();
  (vendors || []).forEach((vendor) => {
    vendorTradeNames(vendor).forEach((name) => {
      const label = categoryOptionLabel(name);
      const key = looseCategoryKey(label);
      if (!label || !key || seen.has(key) || extraSeen.has(key)) return;
      extraSeen.add(key);
      extras.push(label);
    });
  });
  extras.sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }));
  return options.concat(extras);
}

function vendorInCategory(vendor, filter) {
  const wanted = looseCategoryKey(filter);
  if (!wanted) return true;
  return vendorTradeNames(vendor).some((name) => {
    const label = categoryOptionLabel(name) || String(name || '').trim();
    return looseCategoryKey(label) === wanted;
  });
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Trades named in a question, as whole phrases from the map.
 * "electrical" is Electrical. "poolside", "pay", and "handyman" are not.
 */
function tradesInQuestion(question) {
  const text = normalizeCategory(question);
  if (!text) return [];
  const phrases = [];
  const seen = new Set();
  Object.entries(TASK_CATEGORY_TRADES).forEach(([category, trade]) => {
    [category, trade].forEach((label) => {
      const phrase = normalizeCategory(label);
      const key = `${phrase}\0${trade}`;
      if (!phrase || seen.has(key)) return;
      seen.add(key);
      phrases.push({ phrase, trade });
    });
  });
  phrases.sort((a, b) => b.phrase.length - a.phrase.length);
  const found = [];
  phrases.forEach(({ phrase, trade }) => {
    const pattern = new RegExp(`(?:^| )${escapeRegExp(phrase)}(?: |$)`);
    if (pattern.test(text) && !found.includes(trade)) found.push(trade);
  });
  return found;
}

function vendorsForQuestion(question, vendors) {
  const trades = tradesInQuestion(question);
  if (!trades.length) return [];
  const list = Array.isArray(vendors) ? vendors : [];
  return list.filter((vendor) => trades.some((trade) => vendorMatchesTrade(vendor, trade)));
}

module.exports = {
  TASK_CATEGORY_TRADES,
  UNMAPPED_TASK_CATEGORIES,
  tradeForTaskCategory,
  tradesInQuestion,
  vendorMatchesTrade,
  vendorsForQuestion,
  vendorsMatchingTrade,
  primaryVendorTrade,
  extraVendorTrades,
  vendorCategoryOptions,
  vendorInCategory,
};
